// src/utils/checklistAccess.js
// ===========================================================================
// Who sees the Job Checklist / Punch List.
//
// Office roles (Admin, Manager, Assistant ...) always see it - unchanged.
// Techs (employees and subs, role "Tech") are controlled by the Admin from
// Staff Settings:
//
//   appSettings/checklistAccess
//     showToEveryone   true  -> every tech sees the Checklist
//                      false -> only techs switched on below (default)
//     techsCanEditList true  -> those techs can also add / remove / reword
//                               items. Default false: they check items off,
//                               add notes and photos.
//
//   users/{uid}.canSeeChecklist   true -> this tech sees the Checklist
//
// Defaults keep today's behavior: no tech sees it until the Admin turns it on.
// ===========================================================================

import { useEffect, useState } from "react";
import { doc, getDoc, onSnapshot, setDoc, serverTimestamp } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../firebaseConfig";
import { getViewAs } from "./viewAs";

export const CHECKLIST_SETTINGS_DOC = ["appSettings", "checklistAccess"];

export const DEFAULT_CHECKLIST_SETTINGS = Object.freeze({
  showToEveryone: false,
  techsCanEditList: false,
});

/** Roles the setting applies to. Everyone else is office staff and always sees it. */
export const isFieldRole = (role) => String(role || "").trim() === "Tech";

/**
 * Pure rule, so every page answers the same way.
 *   role      the signed-in (or viewed-as) person's role
 *   user      their users/{uid} doc (only canSeeChecklist is read)
 *   settings  appSettings/checklistAccess
 */
export const canSeeChecklist = ({ role, user, settings }) => {
  if (!role) return false;
  if (!isFieldRole(role)) return true;
  const s = { ...DEFAULT_CHECKLIST_SETTINGS, ...(settings || {}) };
  if (s.showToEveryone === true) return true;
  return user?.canSeeChecklist === true;
};

/** Can this person add / remove / reword checklist items (not just check them off)? */
export const canEditChecklistItems = ({ role, settings }) => {
  if (!role) return false;
  if (!isFieldRole(role)) return true;
  return ({ ...DEFAULT_CHECKLIST_SETTINGS, ...(settings || {}) }).techsCanEditList === true;
};

export const saveChecklistSettings = (patch) =>
  setDoc(
    doc(db, ...CHECKLIST_SETTINGS_DOC),
    { ...patch, updatedAt: serverTimestamp(), updatedBy: getAuth().currentUser?.email || "" },
    { merge: true }
  );

/* One live listener on the settings doc, shared by every component. */
let cachedSettings = null;
let settingsListeners = new Set();
let settingsUnsub = null;
const subscribeSettings = (fn) => {
  settingsListeners.add(fn);
  if (cachedSettings) fn(cachedSettings);
  if (!settingsUnsub) {
    settingsUnsub = onSnapshot(
      doc(db, ...CHECKLIST_SETTINGS_DOC),
      (snap) => {
        cachedSettings = { ...DEFAULT_CHECKLIST_SETTINGS, ...(snap.exists() ? snap.data() : {}) };
        settingsListeners.forEach((l) => l(cachedSettings));
      },
      (err) => {
        console.warn("Checklist settings could not be read; using defaults:", err);
        cachedSettings = { ...DEFAULT_CHECKLIST_SETTINGS };
        settingsListeners.forEach((l) => l(cachedSettings));
      }
    );
  }
  return () => {
    settingsListeners.delete(fn);
  };
};

/** Live settings doc (defaults until it loads). */
export const useChecklistSettings = () => {
  const [settings, setSettings] = useState(cachedSettings || DEFAULT_CHECKLIST_SETTINGS);
  useEffect(() => subscribeSettings(setSettings), []);
  return settings;
};

/**
 * { canSee, canEditItems, loading } for the person using the app.
 * `role` is the role the page already knows (it follows "View as"). The
 * person's own users doc is read once to get canSeeChecklist.
 */
export const useChecklistAccess = (role) => {
  const settings = useChecklistSettings();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isFieldRole(role)) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    const load = async (authUser) => {
      const viewAs = getViewAs();
      const uid = viewAs?.docId || viewAs?.uid || authUser?.uid;
      if (!uid) {
        if (!cancelled) setLoading(false);
        return;
      }
      try {
        const snap = await getDoc(doc(db, "users", uid));
        if (!cancelled) setUser(snap.exists() ? snap.data() : null);
      } catch (err) {
        console.warn("Could not read checklist access for this user:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    const auth = getAuth();
    if (auth.currentUser) {
      load(auth.currentUser);
      return () => {
        cancelled = true;
      };
    }
    const unsub = onAuthStateChanged(auth, (u) => {
      if (u) load(u);
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, [role]);

  return {
    canSee: canSeeChecklist({ role, user, settings }),
    canEditItems: canEditChecklistItems({ role, settings }),
    loading,
    settings,
  };
};
